<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import { computed, ref } from 'vue'
import { useEcommercePrice } from '@/composables/game/ecommerce'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import BaseButton from '@/components/base/BaseButton.vue'
import { useConfetti } from '@/composables/game/confetti'
import { syncPlayer, usePlayer } from '@/composables/game/player'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import {
  ECOMMERCE_ITEM_BENEFIT_TYPE,
  type EcommerceItem,
  type EcommerceItemBenefit,
} from '@/types/shared/player-config'
import StarsBalancePill from '@/components/StarsBalancePill.vue'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'
import { allSpecialCurrenciesConfig } from '@/config/special-currencies'
import { formatDuration } from '@/helpers/date'
import { usePack } from '@/composables/game/shop'
import ShopItemDetailsModal from '@/components/modals/ShopItemDetailsModal.vue'
import ShopPurchaseSuccessModal from '@/components/modals/ShopPurchaseSuccessModal.vue'
import { benefitConfigMap, benefitOrder, benefitTypeToKey } from '@/config/benefit-display'
import LimitedPackPurchaseModalBenefits from '@/components/LimitedPackPurchaseModalBenefits.vue'
import Clock5Icon from '@/components/Clock5Icon.vue'
import CrossCircleIcon2 from '@/components/icons/CrossCircleIcon2.vue'

const props = defineProps<{
	show: boolean
	limitedPack: EcommerceItem
}>()

const emit = defineEmits<{
	(e: 'hide'): void
}>()

const { showConfetti } = useConfetti()
const { updateState: updateFortuneWheelState } = useFortuneWheel()
const { getStarPrice } = useEcommercePrice()
const { goldenCoinValue } = usePlayer()

const starPrice = computed(() => getStarPrice(props.limitedPack))
const showPurchaseModal = ref(false)
const showSuccessModal = ref(false)

const { countdownForLimitedPackEnd, remainingPurchaseCount } = usePack(computed(() => props.limitedPack))
const formattedCountdown = computed(() => {
  if (countdownForLimitedPackEnd.value <= 0) return '00:00:00'
  return formatDuration(countdownForLimitedPackEnd.value)
})

interface BenefitDisplayItem {
	colSpan: string
	image: string
	imageClass: string
	alt: string
	value: string | number
	labelClass: string
	tooltipNumber?: number
}

const modalBenefitStyles: Record<string, { imageClass: string; labelClass: string }> = {
  coin: {
    imageClass: 'h-[55px] w-auto',
    labelClass: 'limited-benefit-box-value text-[28px] text-[#10F] font-bold leading-[38px] uppercase text-center -mt-4',
  },
  spin: {
    imageClass: 'h-[55px] w-auto',
    labelClass: 'limited-benefit-box-value text-[28px] text-[#10F] font-bold leading-[38px] uppercase text-center -mt-4',
  },
  tphx2: {
    imageClass: 'h-[55px] w-auto',
    labelClass: 'limited-benefit-box-value text-[28px] text-[#10F] font-bold leading-[38px] uppercase text-center -mt-4',
  },
  offlineProfit: {
    imageClass: 'h-[55px] w-auto',
    labelClass: 'limited-benefit-box-value text-[28px] text-[#10F] font-bold leading-[38px] uppercase text-center -mt-4',
  },
  star: {
    imageClass: 'h-[55px] w-auto',
    labelClass: 'limited-benefit-box-value text-[28px] text-[#10F] font-bold leading-[38px] uppercase text-center -mt-4',
  },
  ticket: {
    imageClass: 'h-[55px] w-auto',
    labelClass: 'limited-benefit-box-value text-[28px] text-[#10F] font-bold leading-[38px] uppercase text-center -mt-4',
  },
}

const getGoldenCoinBenefitAmount = (benefit: EcommerceItemBenefit) =>
  benefit.amount * goldenCoinValue.value

const formatDurationValue = (amount: number) =>
  amount < 1 ? Math.floor(amount * 60) : Math.floor(amount)

const displayedBenefits = computed(() => {
  const result: Array<{ benefit: EcommerceItemBenefit; type: string }> = []

  for (const benefitType of benefitOrder) {
    if (benefitType === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY) {
      props.limitedPack.benefits
        .filter(b => b.type === benefitType)
        .forEach(benefit => result.push({ benefit, type: 'specialCurrency' }))
    } else {
      const benefit = props.limitedPack.benefits.find(b => b.type === benefitType)
      const type = benefitTypeToKey[benefitType]
      if (benefit && type) {
        result.push({ benefit, type })
      }
    }
  }

  return result
})

const getBenefitColSpan = (index: number): string => {
  const total = displayedBenefits.value.length
  if (total === 1) return 'col-span-6'
  if (total === 2) return 'col-span-3'
  if (total === 3) return 'col-span-2'
  // 4+: 2 columns, last item centered if odd
  if (total % 2 === 1 && index === total - 1) return 'col-span-6'
  return 'col-span-3'
}

const benefitDisplayItems = computed<BenefitDisplayItem[]>(() => {
  const items: BenefitDisplayItem[] = []

  displayedBenefits.value.forEach((item, index) => {
    const colSpan = getBenefitColSpan(index)

    if (item.type === 'specialCurrency' && item.benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPECIAL_CURRENCY) {
      items.push({
        colSpan,
        image: allSpecialCurrenciesConfig.value[item.benefit.currency]?.image ?? '',
        imageClass: 'h-[55px] w-auto',
        alt: allSpecialCurrenciesConfig.value[item.benefit.currency]?.label ?? '',
        labelClass: 'limited-benefit-box-value text-[28px] text-[#10F] font-bold leading-[38px] uppercase text-center -mt-4',
        value: formatNumberFullTruncate(item.benefit.amount, false, false, true),
      })
      return
    }

    const config = benefitConfigMap[item.type]
    const styles = modalBenefitStyles[item.type]
    if (!config || !styles) return

    let value: string | number
    let tooltipNumber: number | undefined

    if (item.type === 'coin') {
      value = formatNumberFullTruncate(getGoldenCoinBenefitAmount(item.benefit), false, false, true)
      tooltipNumber = getGoldenCoinBenefitAmount(item.benefit)
    } else if (item.type === 'tphx2' || item.type === 'offlineProfit') {
      value = formatDurationValue(item.benefit.amount)
    } else {
      value = formatNumberFullTruncate(item.benefit.amount, false, false, true)
    }

    items.push({
      colSpan,
      ...config,
      ...styles,
      value,
      ...(tooltipNumber !== undefined && { tooltipNumber }),
    })
  })

  return items
})

async function handlePurchase() {
  showPurchaseModal.value = false
  showSuccessModal.value = true
  await showConfetti()
  await syncPlayer()
  await updateFortuneWheelState()
}

function handleSuccessClose() {
  showSuccessModal.value = false
}
</script>

<template>
  <TheBottomSheet
    :show="show"
    centered
    close-button-class="z-[10] absolute -top-1.5 -right-1.5"
    outer-content-class="overflow-visible rounded-t-[24px] rounded-b-[24px] bg-transparent"
    border-container-class="overflow-visible rounded-t-[24px] rounded-b-[24px]"
    base-modal-class="overflow-visible"
    content-class="pt-8"
    @hide="$emit('hide')"
  >
    <template #closeButton>
      <CrossCircleIcon2 class="limited-pack-close-icon size-8" />
    </template>
    <div
      class="absolute inset-1 bg-cover bg-center bg-no-repeat rounded-[22px] pointer-events-none"
      :style="{ backgroundImage: limitedPack.imageUrl ? `url('${limitedPack.imageUrl}')` : undefined }"
    />
    <div
      class="absolute inset-1 rounded-[22px] pointer-events-none"
      style="background: linear-gradient(0deg, #000 0%, rgba(0, 0, 0, 0.02) 79.81%, rgba(0, 0, 0, 0.00) 100%)"
    />
    <div
      class="w-full flex flex-col items-center relative"
    >
      <StarsBalancePill
        v-if="!limitedPack.config.disableInGameStar"
        class="absolute -left-1 -top-3"
      />
      <!--      <ShopBadge-->
      <!--        :value-image="packValueImage"-->
      <!--        :name-image="packValueNameImage"-->
      <!--        outer-class="-left-2 -top-4 w-[56px] h-auto"-->
      <!--        valueable-image-class="top-1.5 w-[46px]"-->
      <!--        value-image-class="top-[20px] w-[50px]"-->
      <!--        animate-value-image-->
      <!--      />-->
      <div class="flex flex-col items-center">
        <div
          class="text-center text-light-white font-black text-shadow-strong font-pilat max-w-[281px] uppercase text-[48px] leading-[42px]"
          :class="{'mt-9': !limitedPack.config.disableInGameStar}"
        >
          {{ limitedPack.name }}
        </div>
        <div
          v-if="limitedPack.description.length > 0"
          class="mt-2 text-center text-white/60 text-sm font-medium text-shadow-strong"
        >
          {{ limitedPack.description }}
        </div>
      </div>
      <div
        class="flex-1 grid grid-cols-6 gap-4 rounded-[28px] p-3 my-8 glass-effect-container"
      >
        <div
          v-for="(item, index) in benefitDisplayItems"
          :key="index"
          :class="['limited-benefit-box', item.colSpan]"
        >
          <img
            :src="item.image"
            :class="item.imageClass"
            :alt="item.alt"
          >
          <NumberFormatTooltip
            v-if="item.tooltipNumber"
            :number="item.tooltipNumber"
            formatting-type="semi"
            class="z-[3] -mt-4"
          >
            <span :class="item.labelClass">+{{ item.value }}</span>
          </NumberFormatTooltip>
          <span
            v-else
            :class="item.labelClass"
          >
            +{{ item.value }}
          </span>
        </div>
      </div>
      <BaseButton
        style-type="primary-pink"
        class="min-w-[179px] !px-[42px] !py-4 !text-[32px] !leading-7 !tracking-[1px] !overflow-visible"
        :disabled="remainingPurchaseCount === 0"
        @click="showPurchaseModal = true"
      >
        ${{ formatUnshortenedNumber(starPrice.usdAmount, true) }}
      </BaseButton>
      <div class="my-3 text-red-danger text-base flex items-center gap-1.5 font-pilat">
        <Clock5Icon class="size-4 mt-0.5" />
        <div class="red-stroke-white-text min-w-[68px] font-pilat text-base font-bold">
          {{ formattedCountdown }}
        </div>
      </div>
      <div
        v-if="remainingPurchaseCount !== null && remainingPurchaseCount > 0"
        class="mx-auto text-white/[.87] text-center text-base font-semibold [text-shadow:0_0_12px_rgba(0,0,0,0.44)]"
      >
        {{ remainingPurchaseCount }} / {{ limitedPack.config.maxPurchaseCount }} available
      </div>
    </div>
  </TheBottomSheet>

  <ShopItemDetailsModal
    v-if="showPurchaseModal"
    :item="limitedPack"
    :show="showPurchaseModal"
    @hide="showPurchaseModal = false"
    @on-purchase="handlePurchase"
  >
    <template #itemIcon>
      <LimitedPackPurchaseModalBenefits :limited-pack="limitedPack" />
    </template>
  </ShopItemDetailsModal>

  <ShopPurchaseSuccessModal
    v-if="showSuccessModal"
    :item="limitedPack"
    :show="showSuccessModal"
    @hide="handleSuccessClose"
  >
    <template #itemIcon>
      <LimitedPackPurchaseModalBenefits :limited-pack="limitedPack" />
    </template>
  </ShopPurchaseSuccessModal>
</template>

<style scoped lang="scss">
@import "@/assets/styles/ecommerce";

.text-shadow-strong {
	text-shadow: 0 3px 8px rgba(0, 0, 0, 0.55),
	0 8px 20px rgba(0, 0, 0, 0.45);
}

.limited-benefit-box {
	@apply flex flex-col items-center;
}

.limited-benefit-box-value {
	-webkit-text-stroke-width: 5px;
	-webkit-text-stroke-color: #36EEFF;
	paint-order: stroke fill;
	filter: drop-shadow(0 0 24px #10F);
	@apply px-1 z-[3];
}

.glass-effect-container {
	position: relative;
	background: rgba(0, 0, 0, 0.06);
	backdrop-filter: blur(16px);
	border-radius: 28px;
	box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
	-webkit-backdrop-filter: blur(6px);

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: 28px;
		padding: 1px;
		background: linear-gradient(45deg, rgba(255, 255, 255, 0.66) 0%, rgba(153, 153, 153, 0.32) 100%);
		-webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
	}
}

.red-stroke-white-text {
	color: #fff;
	-webkit-text-stroke-width: 7px;
	-webkit-text-stroke-color: #E8405F;
	paint-order: stroke fill;
}
</style>
<style lang="scss">
.limited-pack-close-icon {
	#cross_circle_2_path_1 {
		stroke: #7F7F7F;
	}
}
</style>
